import { FlexBox } from '@airship-platform/jam-web/components';
import { PlusIcon } from '@airship-platform/jam-web/icons';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { useEffect, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { z } from 'zod';

export const ProfileImageInputSchema = z.object({
  profileImage: z.union([
    z
      .instanceof(File)
      .refine((file) => file.size <= 10 * 1024 * 1024, {
        message: 'File size must be less than 10 MB.',
      })
      .refine(
        (file) => ['image/jpeg', 'image/png', 'image/gif'].includes(file.type),
        { message: 'Please upload a JPEG, PNG, or GIF file.' }
      ),
    z.string().url(),
    z.undefined(),
    z.null(),
  ]),
});

type ProfileImageInputs = z.infer<typeof ProfileImageInputSchema>;

const ProfileImageInput: React.FC = () => {
  const { control, watch } = useFormContext<ProfileImageInputs>();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const profileImage = watch('profileImage');

  useEffect(() => {
    if (profileImage instanceof File) {
      const objectUrl = URL.createObjectURL(profileImage);
      setPreviewUrl(objectUrl);

      return () => {
        URL.revokeObjectURL(objectUrl);
        setPreviewUrl(null);
      };
    } else {
      setPreviewUrl(null);
    }
  }, [profileImage]);

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const imageUrl =
    (profileImage instanceof File ? previewUrl : profileImage) || undefined;

  return (
    <Controller
      name="profileImage"
      control={control}
      render={({ field, fieldState }) => (
        <FlexBox flexDirection="column" alignSelf={'flex-start'} gap={2}>
          <input
            accept="image/jpeg,image/png,image/gif"
            style={{ display: 'none' }}
            id="file-upload"
            ref={(element) => {
              fileInputRef.current = element;
              field.ref(element);
            }}
            type="file"
            onChange={(e) => field.onChange(e.target.files?.[0] || null)}
          />
          <FlexBox gap={2}>
            <Typography variant="body3">Profile photo (optional)</Typography>
            {fieldState.error?.message && (
              <Typography variant="body3" color="error">
                {fieldState.error?.message}
              </Typography>
            )}
          </FlexBox>
          {!field.value && (
            <label htmlFor="file-upload">
              <Button
                variant="outlined"
                color="gray"
                size="medium"
                onClick={handleButtonClick}
                startIcon={<PlusIcon />}
              >
                Add Image
              </Button>
            </label>
          )}
          {field.value && (
            <FlexBox alignItems={'center'} gap={3}>
              <Avatar
                src={imageUrl}
                alt="Preview"
                style={{ width: '48px', height: '48px' }}
              />
              <Link
                color="secondary"
                variant="body2"
                onClick={handleButtonClick}
              >
                Replace
              </Link>
            </FlexBox>
          )}
        </FlexBox>
      )}
    />
  );
};

export default ProfileImageInput;
